import * as React from "react";
import { useParams } from "react-router-dom";
import type { Agent } from "@/data/types";
import { trackPresentationEvent } from "@/lib/api";
import { Reveal, Overline } from "./primitives";

interface Props {
  agent: Agent;
  headline?: string;
  body?: string;
}

function displayPhone(raw?: string): string | null {
  if (!raw) return null;
  const digits = raw.replace(/[^\d]/g, "");
  const local = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (local.length === 10) {
    return `+1 (${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
  }
  return raw;
}

function smsHref(raw?: string): string | null {
  if (!raw) return null;
  const digits = raw.replace(/[^\d+]/g, "");
  const normalized = digits.startsWith("+") ? digits : `+${digits.replace(/[^\d]/g, "")}`;
  return `sms:${normalized}`;
}

export const ContactCTA: React.FC<Props> = ({ agent, headline, body }) => {
  const { slug } = useParams<{ slug?: string }>();
  const phoneHref = smsHref(agent.phone);
  const emailHref = agent.email ? `mailto:${agent.email}` : null;
  const scheduleHref = agent.schedulingUrl || null;
  const phoneLabel = displayPhone(agent.phone);
  const telHref = agent.phone
    ? `tel:${agent.phone.replace(/[^\d+]/g, "").replace(/^\+?/, "+")}`
    : null;

  const track = (event: "cta_schedule" | "cta_sms" | "cta_email") => {
    if (!slug) return;
    trackPresentationEvent(slug, event);
  };

  const primaryClasses =
    "inline-flex w-full items-center justify-center rounded-full bg-brass px-8 py-4 text-sm font-medium uppercase tracking-[0.16em] text-charcoal transition-colors duration-300 hover:bg-brass-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass focus-visible:ring-offset-2 focus-visible:ring-offset-ivory sm:w-auto";
  const secondaryClasses =
    "inline-flex w-full items-center justify-center rounded-full border border-charcoal/25 px-8 py-4 text-sm font-medium uppercase tracking-[0.16em] text-charcoal transition-colors duration-300 hover:border-charcoal hover:bg-charcoal hover:text-ivory focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal focus-visible:ring-offset-2 focus-visible:ring-offset-ivory sm:w-auto";

  const hasAnyCta = Boolean(scheduleHref || phoneHref || emailHref);

  return (
    <section
      className="w-full bg-ivory py-24 md:py-32"
      data-testid="contact-cta"
      aria-label="Get in touch"
    >
      <div className="mx-auto w-full max-w-3xl px-6 text-center md:px-10">
        <Reveal>
          <Overline className="mx-auto">Next step</Overline>
          <h2 className="mt-5 font-serif text-4xl leading-tight text-charcoal md:text-5xl lg:text-6xl">
            {headline || "Let's compare notes."}
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-charcoal-soft md:text-lg">
            {body ||
              "If you're still considering a sale, I'd be happy to walk you through how I would approach the relaunch and hear what matters most to you."}
          </p>
        </Reveal>

        <Reveal delay={0.08}>
          <div
            className="mt-12 flex flex-col items-center gap-4"
            data-testid="contact-agent-profile"
          >
            {agent.photoUrl && (
              <img
                src={agent.photoUrl}
                alt={agent.name}
                loading="eager"
                decoding="async"
                className="h-24 w-24 rounded-full object-cover object-top ring-1 ring-charcoal/10 md:h-28 md:w-28"
                data-testid="contact-agent-photo"
              />
            )}
            <div>
              <p className="font-serif text-2xl text-charcoal md:text-3xl">{agent.name}</p>
              {agent.title && (
                <p className="mt-1 text-sm text-charcoal-soft">{agent.title}</p>
              )}
              {agent.team && (
                <p className="mt-1 text-sm text-charcoal-muted">{agent.team}</p>
              )}
              {agent.brokerage && (
                <p className="mt-1 text-xs uppercase tracking-[0.18em] text-charcoal-muted">
                  {agent.brokerage}
                </p>
              )}
              {agent.officeAddress && (
                <p className="mt-2 text-sm text-charcoal-muted">{agent.officeAddress}</p>
              )}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          {hasAnyCta ? (
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row sm:flex-wrap">
              {scheduleHref ? (
                <a
                  href={scheduleHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={primaryClasses}
                  data-testid="cta-schedule"
                  aria-label={`Schedule a 15-minute conversation with ${agent.name}`}
                  onClick={() => track("cta_schedule")}
                >
                  Schedule a 15-Minute Conversation
                </a>
              ) : (
                <span
                  className={`${primaryClasses} cursor-not-allowed opacity-50`}
                  data-testid="cta-schedule-missing"
                >
                  Schedule a 15-Minute Conversation
                </span>
              )}
              {phoneHref && (
                <a
                  href={phoneHref}
                  className={secondaryClasses}
                  data-testid="cta-message"
                  aria-label={`Send ${agent.name} a text message`}
                  onClick={() => track("cta_sms")}
                >
                  Send Me a Message
                </a>
              )}
              {emailHref && (
                <a
                  href={emailHref}
                  className={secondaryClasses}
                  data-testid="cta-email"
                  aria-label={`Send ${agent.name} an email`}
                  onClick={() => track("cta_email")}
                >
                  Send Me an Email
                </a>
              )}
            </div>
          ) : (
            <p
              className="mt-10 text-sm text-charcoal-muted"
              data-testid="cta-missing-config"
            >
              Contact options are being configured for this presentation.
            </p>
          )}
        </Reveal>

        {(phoneLabel || agent.email) && (
          <Reveal delay={0.16}>
            <div className="mt-10 flex flex-col items-center gap-1 text-sm text-charcoal-muted">
              {phoneLabel && telHref && (
                <a
                  href={telHref}
                  className="tracking-wide transition-colors hover:text-charcoal"
                  data-testid="contact-phone"
                >
                  {phoneLabel}
                </a>
              )}
              {agent.email && (
                <a
                  href={`mailto:${agent.email}`}
                  className="tracking-wide transition-colors hover:text-charcoal"
                  data-testid="contact-email"
                >
                  {agent.email}
                </a>
              )}
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};
